import { LocalizedJsonObject } from 'src/models/model'
import Section from 'src/models/page/Section'
import FormValidationMixin from 'src/modules/admin/mixins/FormValidationMixin'

/**
 * This mixin is used to create section.config (JSON object) sub-forms specifically for
 * editing sub-elements of (sub-)arrays within the section.config object. We have several
 * bespoke forms for editing these sub-elements, e.g. GenericCarouselForm, but they share
 * a lot of duplication. Additionally, editing and previewing translatable fields is non-trivial.
 * This mixin standardizes the common functionality and provides utility functions for editing
 * translatable JSON property keys.
 *
 * The component using this mixin must define the following:
 * - field: computed prop that should be the JSON key path to the target array inside section.config
 * - getElementDefaultProps(): method that returns a new object to append to the array
 *
 * Optional but recommended:
 * - typeName: computed prop that returns a string to use as the "model" name for the object
 *   being edited
 */
export default {
  mixins: [FormValidationMixin],
  props: {
    section: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      // See comments for initializeSectionClone()
      sectionClone: null,
      index: null,
      isUpdating: false,
      shown: false
    }
  },
  computed: {
    field () {
      throw new Error('component must define this `field` computed prop that should be the string JSON key path to the target array inside section.config')
    },
    configSubArray () {
      return _.get(this.sectionClone, `config.${this.field}`)
    },
    currentArrayElement () {
      if (!this.configSubArray) return null

      return this.configSubArray[this.index]
    },
    formSaveLabel () {
      return this.isUpdating ? this.updateButtonLabel : this.createButtonLabel
    },
    formTitle () {
      return this.isUpdating ? this.updateFormTitle : this.createFormTitle
    },
    // This should often be overwritten to provide a more specific name for the thing being edited
    typeName () {
      return this.sectionClone?.typeName || ''
    },
    updateButtonLabel () {
      return 'Save'
    },
    updateFormTitle () {
      return `Edit ${this.typeName}`
    },
    createButtonLabel () {
      return 'Create'
    },
    createFormTitle () {
      return `Create ${this.typeName}`
    }
  },
  emits: ['show', 'close', 'save'],
  unmounted () {
    this.destroySectionClone()
  },
  watch: {
    shown () {
      if (this.shown) {
        this.$emit('show')
      } else {
        this.$emit('close')
      }
    }
  },
  methods: {
    getElementDefaultProps () {
      throw new Error('component must define this `getElementDefaultProps()` method that returns a new object to append to the array')
    },
    show (arrayElementIndex = null) {
      this.initializeSectionClone()

      let arrayElement = null
      if (_.isNumber(arrayElementIndex)) {
        arrayElement = this.configSubArray[arrayElementIndex]
      }

      if (arrayElement) {
        this.isUpdating = true
        this.index = arrayElementIndex
      } else {
        this.isUpdating = false
        arrayElement = this.getElementDefaultProps()
        this.configSubArray.push(arrayElement)
        this.index = this.configSubArray.length - 1
      }
      this.shown = true
      this.onShow()
    },
    onShow () {
      // Hook to perform any necessary actions at the end of this.show().
    },
    close () {
      this.destroySectionClone()
      this.index = null
      this.shown = false
    },
    save () {
      this.validateForm(() => {
        this.$emit('save', _.clone(this.currentArrayElement))
        this.close()
      })
    },
    // The components using this mixin provides a form to edit or create a new element, but with
    // the changes not saved until the Admin user clicks the form Save button. So field inputs should
    // not immediately change Section.config values. However, ModelFormField components need to be
    // provided a model instance, which should provide the current language being edited and the fields
    // configuration. This method provides a temporary model instance for the ModelFormFields to use,
    // which should start with the original Section.config.
    initializeSectionClone () {
      this.sectionClone = new Section()
      this.sectionClone.proxy(this.section)
    },
    destroySectionClone () {
      if (this.sectionClone) {
        this.sectionClone.destroy()
        this.sectionClone = null
      }
    },
    // Only necessary for the translatable JSON keys because they aren't automatically localized
    // in Admin app outside of previews.
    localizeKey (key) {
      const localizedKey = LocalizedJsonObject.localizeKey(key, this.section.language)[0]
      return localizedKey
    }
  }
}
